import { useEffect, useState, useCallback } from "react";
import { showAlert } from "../lib/dialog";
import {
  connectSiteGa, listSiteGaProperties, saveSiteGaProperty, disconnectSiteGa,
  type SiteSummary, type GaProperty,
} from "../lib/authClient";

interface Props {
  open: boolean;
  site: SiteSummary | null;
  onClose: () => void;
  onChanged: () => void;
}

// Per-site Google Analytics (GA4) connection. Each saved website connects its own
// Google account and picks a GA4 property. The OAuth code is exchanged server-side
// and the refresh token stored encrypted against the site — it never touches the
// browser here. Powers the "LLM Traffic to Website" section of LLM Mention Tracking.
export function GaConnectDialog({ open, site, onClose, onChanged }: Props) {
  const [configured, setConfigured] = useState<boolean | null>(null); // null = still checking
  const [clientId, setClientId] = useState("");
  const [connected, setConnected] = useState(false);
  const [properties, setProperties] = useState<GaProperty[]>([]);
  const [selected, setSelected] = useState("");
  const [status, setStatus] = useState<{ tone: "info" | "ok" | "err"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const redirectUri = `${window.location.origin}/api/ga/callback`;

  const loadProperties = useCallback(async (siteId: string) => {
    setBusy(true);
    try {
      const props = await listSiteGaProperties(siteId);
      setProperties(props);
      if (!props.length) setStatus({ tone: "info", text: "No GA4 properties found for the connected Google account." });
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load properties." });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !site) return;
    let alive = true;
    setStatus(null);
    setConfigured(null);
    setProperties([]);
    setConnected(site.gaConnected);
    setSelected(site.gaProperty);
    (async () => {
      try {
        const cfg = await (await fetch("/api/ga/config")).json() as { ok: boolean; configured: boolean; clientId: string };
        if (!alive) return;
        setConfigured(cfg.configured);
        setClientId(cfg.clientId ?? "");
        if (cfg.configured && site.gaConnected) await loadProperties(site.id);
      } catch {
        if (alive) { setConfigured(false); setStatus({ tone: "err", text: "Could not reach the server." }); }
      }
    })();
    return () => { alive = false; };
  }, [open, site, loadProperties]);

  if (!open || !site) return null;
  const siteId = site.id;

  // Sign in with Google: open the consent popup with the analytics.readonly scope,
  // receive the auth code via postMessage from /api/ga/callback, exchange + store it.
  function handleConnect() {
    if (!clientId) { void showAlert("Google sign-in isn't configured on this server yet.", { title: "Sign-in Unavailable" }); return; }
    const authUrl = "https://accounts.google.com/o/oauth2/v2/auth?" + new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "https://www.googleapis.com/auth/analytics.readonly",
      access_type: "offline",
      prompt: "consent",
    }).toString();

    const popup = window.open(authUrl, "ga-oauth", "width=520,height=640");
    setStatus({ tone: "info", text: "⏳ Waiting for Google authorization…" });

    const onMessage = async (ev: MessageEvent) => {
      const data = ev.data as { source?: string; code?: string; error?: string } | null;
      if (!data || data.source !== "ga-oauth") return;
      window.removeEventListener("message", onMessage);
      try { popup?.close(); } catch { /* ignore */ }
      if (data.error || !data.code) { setStatus({ tone: "err", text: `⚠ ${data.error || "Authorization cancelled"}` }); return; }
      setStatus({ tone: "info", text: "⏳ Finishing sign-in…" });
      setBusy(true);
      try {
        const props = await connectSiteGa(siteId, data.code, redirectUri);
        setConnected(true);
        setProperties(props);
        setSelected(prev => (prev && props.some(p => p.property === prev)) ? prev : (props[0]?.property ?? ""));
        setStatus({ tone: "ok", text: props.length ? "✓ Connected — choose a property below." : "✓ Connected, but no GA4 properties were found for this account." });
        onChanged();
      } catch (e) {
        setStatus({ tone: "err", text: `⚠ ${e instanceof Error ? e.message : "Sign-in failed"}` });
      } finally {
        setBusy(false);
      }
    };
    window.addEventListener("message", onMessage);
  }

  async function handleSave() {
    setBusy(true);
    try {
      await saveSiteGaProperty(siteId, selected);
      onChanged();
      onClose();
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not save property." });
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setBusy(true);
    try {
      await disconnectSiteGa(siteId);
      setConnected(false);
      setProperties([]);
      setSelected("");
      setStatus({ tone: "info", text: "Disconnected." });
      onChanged();
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not disconnect." });
    } finally {
      setBusy(false);
    }
  }

  const btn: React.CSSProperties = { padding: "8px 18px", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };
  const statusColor = status?.tone === "ok" ? "#16a34a" : status?.tone === "err" ? "#dc2626" : "#2563eb";

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 4200, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "8vh 16px" }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, maxWidth: 600, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.35)", padding: "22px 24px", color: "#334155", lineHeight: 1.5 }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 17, color: "#0f172a", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>
          Google Analytics
        </h2>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12, wordBreak: "break-word" }}>{site.label || site.siteUrl}</div>

        <p style={{ fontSize: 12.5, color: "#475569", margin: "0 0 14px", lineHeight: 1.55 }}>
          Connect this website to Google Analytics (GA4). Sign in with the Google account that has
          access to the property, then choose it. The connection is stored securely on the server and
          used to show referral traffic from AI platforms in the LLM Mention Tracking tab.
        </p>

        {configured === false && (
          <div style={{ fontSize: 12.5, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
            ⚠ Google sign-in isn&rsquo;t configured on this server yet (missing OAuth credentials).
          </div>
        )}

        {configured && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <button
                style={{ ...btn, background: "#fff", color: "#334155", border: "1px solid #cbd5e1", display: "inline-flex", alignItems: "center", gap: 8, opacity: busy ? 0.6 : 1 }}
                onClick={handleConnect}
                disabled={busy}
              >
                {connected ? "Reconnect with Google" : "Sign in with Google"}
              </button>
              {connected && (
                <span style={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>✓ Connected</span>
              )}
            </div>

            {connected && (
              <>
                <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>
                  GA4 Property
                </label>
                <select
                  value={selected}
                  onChange={e => setSelected(e.target.value)}
                  disabled={busy || properties.length === 0}
                  style={{ width: "100%", boxSizing: "border-box", padding: "9px 11px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", background: "#fff", marginBottom: 6 }}
                >
                  {properties.length === 0
                    ? <option value="">{busy ? "Loading properties…" : "No properties available"}</option>
                    : properties.map(p => <option key={p.property} value={p.property}>{p.account ? `${p.account} — ${p.displayName}` : p.displayName}</option>)}
                </select>
              </>
            )}
          </>
        )}

        {status && <div style={{ fontSize: 12.5, color: statusColor, margin: "6px 0 0" }}>{status.text}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
          <div>
            {connected && (
              <button style={{ ...btn, background: "#fff", color: "#dc2626", border: "1px solid #fecaca" }} onClick={() => void handleDisconnect()} disabled={busy}>
                Disconnect
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...btn, background: "#e2e8f0", color: "#334155" }} onClick={onClose} disabled={busy}>Cancel</button>
            <button
              style={{ ...btn, background: "#16a34a", color: "#fff", opacity: (busy || !connected || !selected) ? 0.6 : 1 }}
              onClick={() => void handleSave()}
              disabled={busy || !connected || !selected}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
